import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  adjustSupply,
  createSupply,
  fetchSupplies,
  fetchSupply,
  fetchSupplyHistory,
  replenishSupply,
  saveSupplyPrices,
  updateSupply,
} from './suppliesThunks';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** Every supply that matches the filters; the table pages them itself. */
  supplies: [],
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchSupplies.fulfilled`. */
  requestId: null,
  filters: {
    search: '',
    /** On by default: what was taken out of service is history. */
    onlyActive: true,
    page: 1,
    rowsPerPage: 20,
  },

  /** The supply open on its own page, and the movements that explain its balance. */
  detail: null,
  history: [],
  detailLoading: false,
  detailError: null,

  /** A form is being saved. Its failure is shown inside the form, not here. */
  saving: false,
};

const writes = [createSupply, updateSupply, replenishSupply, adjustSupply, saveSupplyPrices];

const suppliesSlice = createSlice({
  name: 'supplies',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setSupplyFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
    /** Leaving the detail: the next supply must not open on this one. */
    clearSupplyDetail: (state) => {
      state.detail = null;
      state.history = [];
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplies.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchSupplies.fulfilled, (state, action) => {
        // Only the latest request may write: a save reloads the listing while
        // a filter may still be on its way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.supplies = action.payload;
      })
      .addCase(fetchSupplies.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.supplies = [];
      })

      .addCase(fetchSupply.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchSupply.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload.supply;
        state.history = action.payload.movements;
      })
      .addCase(fetchSupply.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })

      .addCase(fetchSupplyHistory.fulfilled, (state, action) => {
        // Asked for a supply that may no longer be the one open.
        if (String(state.detail?.id) === String(action.meta.arg)) state.history = action.payload;
      })

      .addCase(selectWarehouse, (state) => {
        // Another warehouse is another stock of boxes: page 3 may not exist.
        state.filters.page = 1;
      })

      .addMatcher(isAnyOf(...writes.map((thunk) => thunk.pending)), (state) => {
        state.saving = true;
      })
      .addMatcher(isAnyOf(...writes.map((thunk) => thunk.rejected)), (state) => {
        state.saving = false;
      })
      .addMatcher(isAnyOf(...writes.map((thunk) => thunk.fulfilled)), (state, action) => {
        state.saving = false;
        // The open supply takes the answer: its balance, its cost, its prices.
        if (state.detail?.id === action.payload.id) state.detail = action.payload;
      });
  },
});

export const { setSupplyFilters, clearSupplyDetail } = suppliesSlice.actions;

export default suppliesSlice.reducer;
